import axios, { AxiosError } from 'axios'
import config from 'config'
import { isLikeInvoice } from 'utils/bookingSourceHelper'

import { OctaneLogger } from '../../integrations/datadog/OctaneLogger'

interface Asset {
    condition: string
    year: number
    make: string
    model: string
    trim: string
    color?: string
    invoice_price?: number
    booking_source?: string
    mileage?: number
    wholesale_price?: number
    like_invoice_price?: number
}

interface Collateral {
    asset: Asset
    asset_type: string
    asset_value: number
    data_source: string
}

interface Vehicle {
    condition: string
    year: number
    make: string
    model: string
    trim: string
    color?: string
    invoice?: number
    bookingSource?: string
    mileage?: number
    wholesale?: number
    likeInvoice?: number
    lendingValue?: number
}

export interface PostCollateralsAssetsDTO {
    collateral: Collateral[]
}

function getCollateralAssetValue(vehicle: Vehicle): number {
    let assetValue: number | undefined = 0

    if (vehicle.condition === 'new') assetValue = vehicle.invoice
    else if (!isLikeInvoice(vehicle.year, vehicle.bookingSource))
        assetValue = vehicle.wholesale
    else assetValue = vehicle.lendingValue

    return assetValue ?? 0
}

export function createPostCollateralsAssetsDTO(
    vehicle: Vehicle,
): PostCollateralsAssetsDTO {
    const dto: PostCollateralsAssetsDTO = {
        collateral: [
            {
                asset: {
                    condition: vehicle.condition === 'new' ? 'N' : 'U',
                    year: vehicle.year,
                    make: vehicle.make,
                    model: vehicle.model,
                    trim: vehicle.trim,
                    invoice_price: vehicle.invoice,
                    booking_source: vehicle.bookingSource,
                    wholesale_price: vehicle.wholesale,
                    like_invoice_price: vehicle.likeInvoice,
                },
                asset_type: 'automobile',
                asset_value: getCollateralAssetValue(vehicle),
                data_source: 'write_in',
            },
        ],
    }

    // the initial form value for mileage is an empty string
    if (typeof vehicle.mileage === 'number' && vehicle.condition !== 'new') {
        dto.collateral[0].asset.mileage = vehicle.mileage
    }

    return dto
}

export default async function postApplicationCollateralsAssets(
    uuid: string,
    collaterals: PostCollateralsAssetsDTO,
) {
    try {
        const { data } = await axios.post(
            // eslint-disable-next-line max-len
            `${config.API_BASE_URL}/api/v3/applications/${uuid}/collaterals-assets/`,
            collaterals,
            {
                withCredentials: true,
            },
        )

        return data
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error) {
        const axiosError = error as AxiosError

        OctaneLogger.axiosError(
            `Failed to post application collateral assets: ${axiosError.message}`,
            axiosError,
            {
                applicationUuid: uuid,
            },
        )

        throw new Error(axiosError.message)
    }
}
